import { useEffect, useMemo, useState } from "react";
import { getFragmentData } from "@/__generated__";
import {
  ClientServiceProfileFragmentFragmentDoc,
  PackageUnitTypeEnum,
} from "@/__generated__/graphql";
import { ClipboardText, Clock } from "@/components/shared/icons";
import { ListHeading } from "@/components/shared/list-header";
import { ServiceRadioItem } from "@/components/shared/service-radio-item";
import { Typography } from "@/components/shared/typography";
import { Badge, RadioGroup, View } from "@/components/ui";
import { PetSittingCode } from "@/constants/booking";
import { InlineServiceCard } from "@/containers/profile/service-profile/inline-service-card";
import { transformServiceProfileToDetail } from "@/containers/profile/service-profile/lib";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { formatPrice } from "@/lib/helpers/number";
import { useAuthState } from "@/store/auth";
import { useBookingState } from "@/store/booking/useBookingState";
import { AuthModals } from "@/types/users";

import { HelpCard } from "../pet-grooming-booking/help-card";
import { PetProfileModal } from "../pet-grooming-booking/pet-profile-modal";
import { ServiceCreditTopUpCard } from "../components/service-credit-topup-card";
import { Divider } from "@/components/shared/divider";

export const PetSittingBooking = () => {
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const {
    setBookingState,
    data: { packageDetailsFilter, petProfileInfo, packageDetails, selectedPackageIds },
  } = useBookingState();
  const {
    showModal,
    data: { userInfo },
  } = useAuthState();

  const clientId = userInfo.id;

  const { data: profileData } = useClientServiceProfileByFiltersQuery("pet");
  const profiles = profileData?.clientServiceProfileByFilters.clientServiceProfiles ?? [];

  const isDogPet = useMemo(() => {
    return packageDetailsFilter?.unitTypes?.[0] === PackageUnitTypeEnum.Dog;
  }, [packageDetailsFilter?.unitTypes?.[0]]);

  useEffect(() => {
    if (profiles.length > 0 && isDogPet !== undefined) {
      const targetPetType = isDogPet ? "dog" : "cat";
      const profilesWithFragments = profiles.map((profile) => ({
        fragment: getFragmentData(ClientServiceProfileFragmentFragmentDoc, profile),
      }));
      const profilesOfType = profilesWithFragments.filter(({ fragment }) => {
        const petType = (fragment.profileAttributes as { type?: string })?.type;
        return petType === targetPetType;
      });

      if (profilesOfType.length > 0) {
        let selected = profilesOfType.find(({ fragment }) => fragment.primary);

        if (!selected) {
          selected = profilesOfType[0];
        }

        if (selected) {
          setBookingState({
            petProfileInfo: {
              id: selected.fragment.id,
              ...transformServiceProfileToDetail("pet", selected.fragment),
            },
            selectedServiceProfileId: selected.fragment.id,
          });
        }
      }
    }
  }, [profiles, isDogPet]);

  const petSittingPackage = packageDetails.find((pkg) => pkg.code === PetSittingCode);

  const handleChange = () => {
    if (!clientId) {
      showModal(AuthModals.LOGIN);
      return;
    }
    setOpenProfileModal(true);
  };

  return (
    <View className="mt-2 flex flex-col gap-4">
      <View className="mx-4">
        <ServiceCreditTopUpCard service="pet" />
      </View>

      <Divider className="border-b-2" />

      <View className="p-4">
        <View className="flex flex-col gap-2">
          <View className="flex flex-row gap-3">
            <ListHeading title="Pet Profile" icon={ClipboardText} />
            <Badge variant="success" size="sm">
              Required
            </Badge>
          </View>
          <Typography variant="body-sm" color="foreground-intermediate">
            Share a few details so our sitters can provide the perfect care and comfort for your
            furry friend while you’re away.
          </Typography>
        </View>

        <View className="mt-3 flex flex-col gap-2">
          {profiles.length && petProfileInfo ? (
            <InlineServiceCard
              type="pet"
              profile={petProfileInfo}
              onChange={() => setOpenProfileModal(true)}
            />
          ) : (
            <InlineServiceCard type="add" label="Tell us about your pet" onChange={handleChange} />
          )}
        </View>

        <View className="mt-6 flex flex-col gap-2">
          <View className="flex flex-row gap-3">
            <ListHeading title="What does your pet need today?" icon={Clock} />
            <Badge variant="success" size="sm">
              Required
            </Badge>
          </View>
        </View>

        <View className="mt-2 flex flex-col gap-4">
          <Typography variant="body-sm" color="foreground-intermediate">
            Whether it's a walk around the block or cozy care at home, we've got your pet covered.
          </Typography>
        </View>
        <View className="border-secondary-border mt-6 border-b-[2px] pb-6">
          {!!petSittingPackage && (
            <RadioGroup value="true" onValueChange={() => {}}>
              <ServiceRadioItem
                showSparkle
                key={petSittingPackage.code}
                title="Pet Sitting"
                helper="In-home feeding, care & daily exercise"
                subTitle={formatPrice(petSittingPackage.unitValue * petSittingPackage.units)}
                value={String(selectedPackageIds.includes(petSittingPackage.id))}
                selected={selectedPackageIds.includes(petSittingPackage.id)}
                description={isDogPet ? "Includes dog walking during the session" : ""}
                onSelect={() =>
                  setBookingState({
                    selectedPackageIds: [petSittingPackage.id],
                  })
                }
              />
            </RadioGroup>
          )}
        </View>
      </View>
      <HelpCard name="pet-sitting" />

      <PetProfileModal open={openProfileModal} setOpen={setOpenProfileModal} />
    </View>
  );
};
